<template>
  <side-drawer :title="$t('assets.title')" :model-value="true" @update:model-value="emit('closed')">
    <slot name="filter">
      <v-list :loading="!hideCategories && isCategoriesLoading">
        <v-list-item class="py-0">
          <v-checkbox v-model="showRelations" density="comfortable" hide-details :label="$t('map.showRelationLines')" />
        </v-list-item>

        <v-list-group v-if="!hideCategories && !isCategoriesLoading">
          <template #activator="{ props: itemProps }">
            <v-list-item v-bind="itemProps">
              <template #title>
                <span>{{ $t("categories") }}</span>
                <v-badge
                  v-if="selectedCategories.length !== availableCategories.length"
                  inline
                  color="error"
                  :content="selectedCategories.length"
                />
              </template>
            </v-list-item>
          </template>

          <template #default>
            <v-list-item v-for="category of availableCategories" :key="category._id" class="py-0">
              <v-checkbox v-model="selectedCategories" hide-details density="comfortable" :value="category._id">
                <template #label>
                  <category-badge :category="category" />&nbsp;<span>({{ categoriesCount.get(category._id) }})</span>
                </template>
              </v-checkbox>
            </v-list-item>
          </template>
        </v-list-group>
      </v-list>
    </slot>
  </side-drawer>
</template>

<script setup lang="ts">
const assetCategoryStore = useAssetCategoryStore()
const authStore = useAuthStore()

const { currentOrganization } = storeToRefs(authStore)

const showRelations = useLocalStorage<boolean>("ol:asset-relations", false)
const selectedCategories = defineModel<Array<string>>({ default: () => [] })

defineProps<{
  hideCategories?: boolean
}>()

const emit = defineEmits<{
  (e: "closed"): void
}>()

const { data: availableCategories, status: categoryStatus } = useLazyAsyncData(
  `${currentOrganization.value}:filter-categories`,
  () => assetCategoryStore.getUsedCategories(),
  {
    default: () => [] as Array<AssetCategory>,
  },
)

const { data: categoriesCount, status: categoryCountStatus } = useLazyAsyncData(
  `${currentOrganization.value}:filter-categories-count`,
  async () => {
    const counts = await assetCategoryStore.getCategoryAssetCount()
    return new Map(counts.map((count) => [count._id, count.count]))
  },
  {
    default: () => new Map<string, number>(),
  },
)

const isCategoriesLoading = computed(() => categoryStatus.value === "pending" || categoryCountStatus.value === "pending")

watchOnce([availableCategories], () => {
  // Pre-select all categories if none are selected
  if (availableCategories.value.length > 0 && selectedCategories.value.length === 0) {
    selectedCategories.value = availableCategories.value.map((category) => category._id)
  }
})
</script>
